import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, Wind, Sun, CheckCircle, ArrowRight, MapPin, Award, Shield, Phone, Mail, ChevronDown, ChevronUp, Search, Settings, FileCheck, Wrench, HeadphonesIcon, } from 'lucide-react';
import Image from '@/components/ui/Image';
import AnimatedSection from '@/components/ui/AnimatedSection';
import SectionTitle from '@/components/ui/SectionTitle';
import Footer from '@/components/sections/Footer';
import { useProjects } from '@/hooks/useProjects';
const serviceFlow = [
    {
        step: '01',
        title: 'ヒアリング・現地調査',
        description: 'お客様のご要望を詳しくお聞きし、現地調査を実施',
        icon: Search,
    },
    {
        step: '02',
        title: '設計・見積提案',
        description: '最適なソリューションを設計し、詳細見積をご提案',
        icon: FileCheck,
    },
    {
        step: '03',
        title: '契約・工程調整',
        description: 'ご契約後、工事スケジュールを綿密に調整',
        icon: CheckCircle,
    },
    {
        step: '04',
        title: '施工・品質管理',
        description: '安全第一で施工を実施、厳格な品質管理を徹底',
        icon: Settings,
    },
    {
        step: '05',
        title: '検査・試運転',
        description: '完成検査と試運転で性能を確認',
        icon: Wrench,
    },
    {
        step: '06',
        title: 'アフターサポート',
        description: '定期メンテナンスと24時間緊急対応',
        icon: HeadphonesIcon,
    },
];
const certifications = [
    '電気工事業許可（岩手県知事許可）',
    '管工事業許可（岩手県知事許可）',
    '第一種電気工事士',
    '1級電気工事施工管理技士',
    '1級管工事施工管理技士',
    '第二種電気工事士',
    '消防設備士',
];
const serviceAreas = [
    { name: '岩手県', coverage: '全域対応' },
    { name: '青森県', coverage: '全域対応' },
    { name: '秋田県', coverage: '全域対応' },
    { name: '宮城県', coverage: '全域対応' },
    { name: '山形県', coverage: '全域対応' },
    { name: '福島県', coverage: '全域対応' },
];
const faqs = [
    {
        question: '見積もりは無料ですか？',
        answer: 'はい、お見積もりは無料で承っております。現地調査も含めて費用は一切かかりません。',
    },
    {
        question: '営業時間外の緊急対応は可能ですか？',
        answer: '24時間365日の緊急対応体制を整えております。設備トラブルの際はお気軽にご連絡ください。',
    },
    {
        question: 'アフターサービスはありますか？',
        answer: '工事完了後も定期メンテナンスサービスを提供しております。保証期間内の不具合は無償で対応いたします。',
    },
    {
        question: '他県での工事も対応可能ですか？',
        answer: '東北一円での工事に対応しております。遠方の場合は事前にご相談ください。',
    },
];
const detailedServices = [
    {
        id: 'electrical',
        title: 'ELECTRICAL WORK',
        titleJa: '電気設備工事',
        description: '商業施設から工場まで、あらゆる建物の電気設備を安全・確実に施工',
        image: '/images/services/service-1.webp',
        features: [
            '屋内外配線工事',
            '幹線・分電盤設置',
            'LED照明工事',
            '動力設備工事',
            '防災設備工事',
            '太陽光連系工事',
        ],
        qualifications: ['第一種電気工事士', '1級電気工事施工管理技士'],
        achievements: '年間50件以上の施工実績',
        icon: Zap,
    },
    {
        id: 'piping',
        title: 'PIPING & HVAC',
        titleJa: '空調・衛生設備工事',
        description: '快適な室内環境を実現する空調・衛生設備の設計から施工まで',
        image: '/images/services/service-2.webp',
        features: [
            '空調設備工事',
            '給排水設備工事',
            '衛生設備工事',
            '消火設備工事',
            'ダクト工事',
            '配管保温工事',
        ],
        qualifications: ['1級管工事施工管理技士', '消防設備士'],
        achievements: '大型商業施設での豊富な実績',
        icon: Wind,
    },
    {
        id: 'solar',
        title: 'SOLAR PV SYSTEM',
        titleJa: '太陽光発電システム',
        description: '環境とコストの両面でメリットをもたらす太陽光発電システム',
        image: '/images/services/service-3.webp',
        features: [
            'システム設計・提案',
            'パネル設置工事',
            'パワコン設置',
            '系統連系工事',
            'メンテナンス',
            '発電量監視',
        ],
        qualifications: ['電気工事士', '太陽光発電アドバイザー'],
        achievements: '累計発電容量1MW以上の実績',
        icon: Sun,
    },
];
export default function ServicesPage() {
    const [openFaq, setOpenFaq] = useState(null);
    const { projects } = useProjects();
    return (<main className='min-h-screen bg-white scroll-smooth'>
      {/* Hero Section */}
      <section className='relative h-screen overflow-hidden snap-start'>
        <div className='absolute inset-0'>
          <Image src='/images/services/service-hero.webp' alt='電気設備工事の様子' className='w-full h-full object-cover' priority/>
          <div className='absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/40 to-navy/70'/>
        </div>

        <div className='relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-24'>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className='max-w-4xl'>
            <h1 className='text-6xl md:text-7xl lg:text-8xl text-white font-bebas tracking-wider leading-tight text-transparent bg-clip-text bg-gradient-to-r from-white to-white drop-shadow-lg'>
              SERVICE
            </h1>
            <p className='mt-6 text-2xl md:text-3xl text-white font-noto font-light'>
              ワンストップで提供する
              <br />
              3つの設備ソリューション
            </p>
            <p className='mt-6 text-lg text-white/90 max-w-3xl leading-relaxed'>
              盛電設備工業株式会社は、各種配管工事・電気設備工事・太陽光発電工事をワンストップで提供する総合設備企業です。
              豊富な経験と確かな技術で、お客様のニーズに最適なソリューションをご提案いたします。
            </p>
          </motion.div>
        </div>

        <motion.div className='absolute bottom-10 left-1/2 -translate-x-1/2 z-10' initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1 }}>
          <a href='#services' className='w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center transition-all hover:-translate-y-1 hover:bg-white/20' aria-label='サービス詳細へスクロール'>
            <ChevronDown className='text-white' size={24}/>
          </a>
        </motion.div>
      </section>

      {/* Main Services Section */}
      <AnimatedSection id='services' className='bg-white snap-start'>
        <div className='section-container'>
          <SectionTitle title='OUR SERVICES' subtitle='主要サービス' description='電気・空調・太陽光発電の3つの専門分野で、設計から施工、メンテナンスまで一貫したサービスを提供します。' outlined/>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-8 mt-16'>
            {detailedServices.map((service, index) => (<motion.div key={service.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className='group bg-lightgray rounded-lg overflow-hidden hover:shadow-xl transition-all duration-300'>
                <div className='relative aspect-[4/3] overflow-hidden'>
                  <Image src={service.image} alt={service.title} className='object-cover transition-transform duration-500 group-hover:scale-105'/>
                  <div className='absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent'/>
                  <div className='absolute top-4 left-4'>
                    <div className='w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center'>
                      <service.icon className='w-6 h-6 text-white'/>
                    </div>
                  </div>
                  <div className='absolute bottom-4 left-4 right-4'>
                    <h3 className='text-2xl font-bebas text-white tracking-wide'>
                      {service.title}
                    </h3>
                    <p className='text-lg font-noto text-white/90 mt-1'>
                      {service.titleJa}
                    </p>
                  </div>
                </div>

                <div className='p-6'>
                  <p className='text-navy/80 mb-6 leading-relaxed'>
                    {service.description}
                  </p>

                  <div className='space-y-4'>
                    <div>
                      <h4 className='font-medium text-navy mb-3'>主な業務範囲</h4>
                      <ul className='grid grid-cols-1 gap-2 text-sm text-navy/70'>
                        {service.features.map((feature, idx) => (<li key={idx} className='flex items-center'>
                            <CheckCircle className='w-3 h-3 text-skyblue mr-2 shrink-0'/>
                            {feature}
                          </li>))}
                      </ul>
                    </div>

                    <div>
                      <h4 className='font-medium text-navy mb-2'>専門資格</h4>
                      <div className='flex flex-wrap gap-2'>
                        {service.qualifications.map((qual, idx) => (<span key={idx} className='px-2 py-1 bg-skyblue/10 text-skyblue text-xs rounded-full'>
                            {qual}
                          </span>))}
                      </div>
                    </div>

                    <div className='pt-2 border-t border-navy/10'>
                      <p className='text-sm text-navy/60 font-medium'>
                        {service.achievements}
                      </p>
                    </div>
                  </div>

                  <div className='mt-6'>
                    <Link to='/works' className='group flex items-center text-navy font-medium transition-colors hover:text-skyblue'>
                      <span>詳細ページへ</span>
                      <ArrowRight size={16} className='ml-1 transition-transform group-hover:translate-x-1'/>
                    </Link>
                  </div>
                </div>
              </motion.div>))}
          </div>
        </div>
      </AnimatedSection>

      {/* Service Flow */}
      <AnimatedSection className='bg-gray-50 snap-start'>
        <div className='section-container'>
          <SectionTitle title='SERVICE FLOW' subtitle='サービスの流れ' description='お問い合わせから完成まで、6つのステップで安心・確実な施工をお約束します。'/>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16'>
            {serviceFlow.map((step, index) => (<motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className='relative'>
                <div className='bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow'>
                  <div className='flex items-start gap-4'>
                    <div className='w-12 h-12 rounded-full bg-navy flex items-center justify-center shrink-0'>
                      <step.icon className='w-6 h-6 text-white'/>
                    </div>
                    <div className='flex-1'>
                      <div className='flex items-center gap-2 mb-2'>
                        <span className='text-2xl font-bebas text-skyblue'>
                          {step.step}
                        </span>
                        <h3 className='text-lg font-medium text-navy'>
                          {step.title}
                        </h3>
                      </div>
                      <p className='text-navy/70 text-sm leading-relaxed'>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>

                {index < serviceFlow.length - 1 && index % 3 !== 2 && (<div className='hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-navy/20'/>)}
              </motion.div>))}
          </div>

          <div className='mt-16 bg-navy p-8 rounded-lg text-center'>
            <h3 className='text-2xl font-bebas text-white mb-4'>
              ONESTOP SOLUTION
            </h3>
            <p className='text-white/90 max-w-3xl mx-auto leading-relaxed'>
              複数の専門業者に依頼する必要がなく、設計から施工、メンテナンスまで一貫してお任せいただけます。
              工期短縮とコスト削減を実現し、責任の所在も明確です。
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Trust & Reliability */}
      <AnimatedSection className='bg-white snap-start'>
        <div className='section-container'>
          <SectionTitle title='TRUST & RELIABILITY' subtitle='信頼性の証明' outlined/>

          <div className='grid grid-cols-1 lg:grid-cols-3 gap-12 mt-16'>
            {/* Service Areas */}
            <div>
              <h3 className='text-2xl font-bebas text-navy mb-6'>
                <MapPin className='inline w-6 h-6 mr-2'/>
                対応エリアマップ
              </h3>
              <div className='space-y-3'>
                {serviceAreas.map((area, index) => (<motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: index * 0.1 }} viewport={{ once: true }} className='flex justify-between items-center p-3 bg-lightgray rounded hover:bg-skyblue/10 transition-colors'>
                    <span className='font-medium text-navy'>{area.name}</span>
                    <span className='text-sm text-navy/70'>{area.coverage}</span>
                  </motion.div>))}
              </div>
              <p className='text-sm text-navy/60 mt-4'>
                ※その他の地域もご相談ください
              </p>
            </div>

            {/* Certifications */}
            <div>
              <h3 className='text-2xl font-bebas text-navy mb-6'>
                <Award className='inline w-6 h-6 mr-2'/>
                保有資格・許認可一覧
              </h3>
              <div className='space-y-2'>
                {certifications.map((cert, index) => (<motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }} viewport={{ once: true }} className='flex items-center'>
                    <CheckCircle className='w-4 h-4 text-green-500 mr-3 shrink-0'/>
                    <span className='text-sm text-navy/80'>{cert}</span>
                  </motion.div>))}
              </div>
            </div>

            {/* Quality & Safety */}
            <div>
              <h3 className='text-2xl font-bebas text-navy mb-6'>
                <Shield className='inline w-6 h-6 mr-2'/>
                安全管理・品質管理
              </h3>
              <div className='space-y-4'>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} viewport={{ once: true }} className='p-4 bg-lightgray rounded'>
                  <h4 className='font-medium text-navy mb-2'>安全管理の取り組み</h4>
                  <ul className='text-sm text-navy/70 space-y-1'>
                    <li>• 毎朝の安全ミーティング実施</li>
                    <li>• 安全装備の徹底着用</li>
                    <li>• 定期的な安全教育</li>
                  </ul>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }} className='p-4 bg-lightgray rounded'>
                  <h4 className='font-medium text-navy mb-2'>品質管理の取り組み</h4>
                  <ul className='text-sm text-navy/70 space-y-1'>
                    <li>• 工程ごとの品質チェック</li>
                    <li>• 完成検査の徹底実施</li>
                    <li>• アフターフォロー体制</li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Project Gallery */}
      <AnimatedSection className='bg-gray-50 snap-start'>
        <div className='section-container'>
          <SectionTitle title='PROJECT GALLERY' subtitle='実績ギャラリー'/>

          <div className='mt-16'>
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {projects.filter(p => p.status === 'published').slice(0, 6).map((project, index) => (<motion.div key={project.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className='group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300'>
                  <Link to={`/works/${project.slug}`}>
                    <div className='relative aspect-[4/3] overflow-hidden'>
                      <Image src={project.image_url} alt={project.title} className='object-cover transition-transform duration-500 group-hover:scale-105'/>
                      <div className='absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300'/>
                      <div className='absolute top-4 left-4'>
                        <span className='px-3 py-1 bg-white/90 text-navy text-sm rounded-full font-medium'>
                          {project.category}
                        </span>
                      </div>
                    </div>
                    <div className='p-6'>
                      <h3 className='text-lg font-medium text-navy group-hover:text-skyblue transition-colors mb-2'>
                        {project.title}
                      </h3>
                      <p className='text-sm text-navy/60 mb-3'>{project.location}</p>
                      <p className='text-sm text-navy/70 line-clamp-2'>
                        {project.description}
                      </p>
                    </div>
                  </Link>
                </motion.div>))}
            </div>

            <div className='text-center mt-12'>
              <Link to='/works' className='inline-flex items-center px-8 py-3 bg-navy text-white rounded-full font-medium transition-all hover:bg-opacity-90'>
                すべての施工事例を見る
                <ArrowRight className='ml-2 w-4 h-4'/>
              </Link>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* FAQ Section */}
      <AnimatedSection className='bg-white snap-start'>
        <div className='section-container'>
          <SectionTitle title='FAQ' subtitle='よくあるご質問' outlined/>

          <div className='max-w-3xl mx-auto mt-16'>
            {faqs.map((faq, index) => (<motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }} viewport={{ once: true }} className='border-b border-navy/10 last:border-b-0'>
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className='w-full py-6 flex items-center justify-between text-left hover:text-skyblue transition-colors'>
                  <span className='font-medium text-navy pr-4'>
                    {faq.question}
                  </span>
                  {openFaq === index ? (<ChevronUp className='w-5 h-5 text-navy/40 shrink-0'/>) : (<ChevronDown className='w-5 h-5 text-navy/40 shrink-0'/>)}
                </button>
                {openFaq === index && (<motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className='pb-6'>
                    <p className='text-navy/70 leading-relaxed'>{faq.answer}</p>
                  </motion.div>)}
              </motion.div>))}
          </div>
        </div>
      </AnimatedSection>

      {/* Contact CTA */}
      <AnimatedSection className='bg-navy snap-start'>
        <div className='section-container'>
          <div className='text-center'>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
              <h2 className='text-6xl md:text-7xl font-bebas tracking-wider text-white text-transparent bg-clip-text bg-gradient-to-r from-white to-white drop-shadow-lg'>
                GET STARTED
              </h2>
              <p className='text-xl font-noto text-white/80 mt-6 max-w-2xl mx-auto'>
                お見積り・ご相談は無料です。
                <br />
                お気軽にお問い合わせください。
              </p>

              <div className='mt-12 flex flex-col sm:flex-row gap-4 justify-center'>
                <Link to='/contact' className='inline-flex items-center px-8 py-4 bg-white text-navy rounded-full font-medium transition-all hover:bg-opacity-90'>
                  お問い合わせフォーム
                  <ArrowRight className='ml-2 w-4 h-4'/>
                </Link>
                <a href='tel:019-681-3977' className='inline-flex items-center px-8 py-4 border border-white/30 text-white rounded-full font-medium transition-all hover:bg-white/10'>
                  <Phone className='w-4 h-4 mr-2'/>
                  019-681-3977
                </a>
              </div>

              <div className='mt-8 text-white/60 text-sm'>
                <p className='font-medium'>営業時間と連絡先情報</p>
                <p className='mt-2'>受付時間: 平日 9:00〜18:00</p>
                <p className='mt-1'>
                  <Mail className='w-4 h-4 inline mr-1'/>
                  info@moriden-setubi.com
                </p>
                <p className='mt-1'>
                  <MapPin className='w-4 h-4 inline mr-1'/>
                  〒020-0839 岩手県盛岡市津志田南3-13-15
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      <Footer />
    </main>);
}
